import { App, Flex, Form } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { getApiError, patchSchedulerSetSchedulesByPoints } from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";

import { useAsyncAction } from "@/hooks";
import { useAppDispatch } from "@/hooks/redux";
import { ScheduleRepeatForm } from "@/pages/SV/RouteManagement/components/ScheduleRepeatForm";
import { ScheduleRepeatFormFields } from "@/pages/SV/RouteManagement/components/ScheduleRepeatForm/ScheduleRepeatForm.types";
import {
    RouteEditorModal,
    RouteEditorModalPayloads,
} from "@/pages/SV/RouteManagement/RouteEditor/RouteEditor.types";
import BaseButton from "@/shared/ui/BaseButton";
import { BaseModal } from "@/shared/ui/BaseModal";
import { updateSchedule } from "@/store/slices/manager";

interface EditScheduleModalProps {
    onClose: () => void;
    payload: RouteEditorModalPayloads[RouteEditorModal.EditSchedule];
    onApprove: () => Promise<void>;
}

export const EditScheduleModal = ({
    payload,
    onClose,
    onApprove,
}: EditScheduleModalProps) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();

    const { schedule } = payload;

    const [hasFormErrors, setHasFormErrors] = useState(false);

    const [form] = Form.useForm<ScheduleRepeatFormFields>();

    useEffect(() => {
        form.setFieldsValue({
            weekly: schedule.weekly ?? 1,
            days: schedule.days ?? [],
            weekStartDate:
                schedule.startDate ??
                dayjs().startOf("week").format(backendDateFormat.date),
        });
    }, [schedule, form]);

    const [handleSave, saveLoading] = useAsyncAction(
        async (values: ScheduleRepeatFormFields) => {
            const { weekly, days, weekStartDate } = values;

            const updatedPoint = {
                ...schedule,
                weekly,
                days,
                startDate: weekStartDate,
            };

            if (updatedPoint.days.length === 0) {
                notification.error({
                    title: "Оберіть хоча б один день повторення.",
                });
                return;
            }

            if (payload.createMode) {
                dispatch(
                    updateSchedule({
                        pointId: updatedPoint.pointId,
                        updatedData: updatedPoint,
                    }),
                );
                onClose();
                return;
            }

            const { allSchedules, route } = payload as Extract<
                typeof payload,
                { createMode: false }
            >;

            const updatedSchedules = allSchedules.map((item) =>
                item.pointId === updatedPoint.pointId
                    ? { ...item, ...updatedPoint }
                    : item,
            );

            try {
                patchSchedulerSetSchedulesByPoints({
                    routeId: route.uuid,
                    schedules: updatedSchedules,
                });

                onClose();
                await onApprove();
            } catch (error) {
                const { displayMessage } = getApiError(error);
                notification.error({
                    title: "Не вдалося редагувати",
                    description: displayMessage,
                });
            }
        },
    );

    return (
        <BaseModal
            open
            onClose={onClose}
            actions={null}
            mask={{ closable: !saveLoading }}
        >
            <Flex vertical gap={16}>
                <Form
                    form={form}
                    onFinish={handleSave}
                    disabled={saveLoading}
                    onFieldsChange={() => {
                        setHasFormErrors(
                            form.getFieldsError().some((f) => f.errors.length),
                        );
                    }}
                >
                    <ScheduleRepeatForm form={form} disabled={saveLoading} />
                </Form>

                <BaseButton
                    type="primary"
                    onClick={() => form.submit()}
                    loading={saveLoading}
                    disabled={hasFormErrors}
                >
                    Зберегти зміни
                </BaseButton>
            </Flex>
        </BaseModal>
    );
};
